import React, { useState, useEffect } from "react";
import "../../../../../css/CashflowModeller2/Inputs/DisplayMedium.css";
import { useFormattedNumber } from "../../Hooks/UseFormattedNumber";

/**
 * DisplayMedium is a controlled input component for displaying and editing text or formatted numbers.
 * It supports optional currency formatting (e.g., £), disables input, and displays a label.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isDisabled - If true, disables the input field.
 * @param {"text"|"number"} [props.type="text"] - The type of input, either "text" or "number".
 * @param {string} [props.label] - Optional label to display above the input.
 * @param {string|number} [props.value] - The value to display in the input.
 * @param {function} [props.onChange] - Callback fired when the input value changes.
 * @param {boolean} [props.currency=false] - If true and type is "number", formats the value as currency (£).
 *
 * @returns {JSX.Element} The rendered input component with optional label and formatting.
 */
const DisplayMedium = ({
  isDisabled,
  type = "text",
  label,
  value,
  onChange,
  currency = false, // support for £
}) => {
  const [status, setStatus] = useState("default");
  const {
    rawValue,
    formattedValue,
    handleChange: handleFormattedChange,
    setRawValue,
  } = useFormattedNumber(value || "", type);
  const [textValue, setTextValue] = useState(value || "");

  /**
   * Formats a given value as a string with commas as thousand separators and exactly two decimal places.
   *
   * If the input is a number, it is formatted directly. If the input is not a number,
   * it is parsed as a float before formatting.
   *
   * @param {number|string} val - The value to format. Can be a number or a string representing a number.
   * @returns {string} The formatted string with commas and two decimal places.
   */
  const formatWithCommas = (val) =>
    typeof val === "number"
      ? val.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : parseFloat(val).toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  /**
   * Handles changes to a text input field.
   * Updates the local state with the new value and calls the optional onChange callback.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   */
  const handleTextChange = (e) => {
    const val = e.target.value;
    setTextValue(val);
    if (onChange) onChange(val);
  };

  /**
   * Returns a formatted display value based on the input type and value.
   *
   * - If the input type is not "number", returns the text value as is.
   * - If the value is falsy (e.g., null, undefined, 0, ""), returns an empty string.
   * - If the value is a number, formats it with commas.
   * - If the currency flag is true, prefixes the formatted number with the pound symbol (£).
   *
   * @returns {string} The formatted display value.
   */
  const display = () => {
    if (type !== "number") return textValue;
    if (!value) return "";
    const formatted = formatWithCommas(value);
    return currency ? `£${formatted}` : formatted;
  };

  
  useEffect(() => {
    if (type !== "number") {
      setTextValue(value || "");
    } else {
      setRawValue(value || "");
    }
  }, [value, type, setRawValue]);

  return (
    <div className={`${label ? "input-label-container" : ""}`}>
      {label && (
        <div className="Label" style={{ textAlign: "center" }}>
          {label}
        </div>
      )}

      <div className={`input-container ${isDisabled ? "disabled" : ""}`}>
        <input
          type="text"
          inputMode={type === "number" ? "decimal" : "text"}
          pattern={type === "number" ? "\\d*\\.?\\d*" : undefined}
          placeholder={type === "number" ? "£18,000" : "William"}
          value={display()}
          onChange={
            type === "number" ? handleFormattedChange : handleTextChange
          }
          disabled={isDisabled}
          onFocus={() => setStatus("selected")}
          onBlur={() => setStatus("default")}
          className={`display-medium input-field Input ${
            !value ? "placeholder" : ""
          } ${isDisabled ? "disabled" : ""}`}
        />
      </div>
    </div>
  );
};

export default DisplayMedium;
